import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const SkillsSection = ({ formData, onChange, errors }) => {
  const [skillInput, setSkillInput] = useState('');
  const [selectedProficiency, setSelectedProficiency] = useState('intermediate');

  const proficiencyLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'expert', label: 'Expert' }
  ];

  const suggestedSkills = [
    'React', 'JavaScript', 'TypeScript', 'Node.js', 'Python', 'Java',
    'HTML/CSS', 'SQL', 'MongoDB', 'AWS', 'Docker', 'Git',
    'REST APIs', 'GraphQL', 'Redux', 'Next.js', 'Express.js', 'Spring Boot'
  ];

  const addSkill = () => {
    if (skillInput?.trim()) {
      const newSkill = {
        name: skillInput?.trim(),
        proficiency: selectedProficiency,
        required: true
      };
      onChange('skills', [...(formData?.skills || []), newSkill]);
      setSkillInput('');
      setSelectedProficiency('intermediate');
    }
  };

  const addSuggestedSkill = (skillName) => {
    const newSkill = {
      name: skillName,
      proficiency: 'intermediate',
      required: true
    };
    onChange('skills', [...(formData?.skills || []), newSkill]);
  };

  const removeSkill = (index) => {
    const newSkills = formData?.skills?.filter((_, i) => i !== index);
    onChange('skills', newSkills);
  };

  const toggleRequired = (index) => {
    const newSkills = [...formData?.skills];
    newSkills[index].required = !newSkills?.[index]?.required;
    onChange('skills', newSkills);
  };

  const getProficiencyColor = (proficiency) => {
    const colors = {
      beginner: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      intermediate: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      advanced: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      expert: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
    };
    return colors?.[proficiency] || colors?.intermediate;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
          <Icon name="Code2" size={20} color="var(--color-success)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Skills & Competencies</h2>
          <p className="text-sm text-muted-foreground">Technical and soft skills required</p>
        </div>
      </div>
      <div className="space-y-6">
        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground">
            Add Skills <span className="text-error">*</span>
          </label>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={skillInput}
              onChange={(e) => setSkillInput(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), addSkill())}
              placeholder="Enter skill name"
              className="flex-1 px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="w-full sm:w-48">
              <Select
                options={proficiencyLevels}
                value={selectedProficiency}
                onChange={setSelectedProficiency}
                placeholder="Proficiency"
              />
            </div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={addSkill}
            >
              Add
            </Button>
          </div>

          {errors?.skills && (
            <p className="text-sm text-error">{errors?.skills}</p>
          )}
        </div>

        {formData?.skills && formData?.skills?.length > 0 && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-foreground">
              Added Skills ({formData?.skills?.length})
            </label>
            <div className="space-y-2">
              {formData?.skills?.map((skill, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Icon name="Check" size={16} color="var(--color-success)" />
                    <span className="text-sm font-medium text-foreground">{skill?.name}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getProficiencyColor(skill?.proficiency)}`}>
                      {skill?.proficiency}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleRequired(index)}
                      className={`px-2 py-1 rounded text-xs font-medium transition-smooth ${
                        skill?.required
                          ? 'bg-error/10 text-error' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      {skill?.required ? 'Required' : 'Preferred'}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSkill(index)}
                    className="text-error hover:text-error/80 transition-smooth"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Suggested Skills
          </label>
          <div className="flex flex-wrap gap-2">
            {suggestedSkills?.filter(skill => !formData?.skills?.some(s => s?.name === skill))?.map((skill) => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => addSuggestedSkill(skill)}
                  className="px-3 py-1.5 bg-primary/5 hover:bg-primary/10 border border-primary/20 rounded-lg text-sm text-primary transition-smooth"
                >
                  + {skill}
                </button>
              ))}
          </div>
        </div>

        <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">Skill Requirements Tips</p>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Mark essential skills as "Required" and nice-to-have skills as "Preferred"</li>
                <li>Be realistic about proficiency levels to attract qualified candidates</li>
                <li>Include both technical and soft skills for comprehensive evaluation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SkillsSection;